import React from 'react'
import { motion } from 'framer-motion'
import { Line, Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js'
import { format } from 'date-fns'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
)

const DCAHistoricalResult = ({ result }) => {
  if (!result) return null

  const data = result
  const formattedDate = format(new Date(data.timestamp), 'PPpp')

  // Extract chart data
  const labels = data.orders.map(o => o.date)
  const avgPrices = data.orders.map(o => o.average_price)
  const marketPrices = data.orders.map(o => o.price)
  const quantities = data.orders.map(o => o.quantity)

  const lineChartData = {
    labels,
    datasets: [
      {
        label: 'Market Price',
        data: marketPrices,
        borderColor: '#60a5fa',
        backgroundColor: '#60a5fa30',
        fill: false,
        tension: 0.3
      },
      {
        label: 'Average DCA Price',
        data: avgPrices,
        borderColor: '#facc15',
        backgroundColor: '#facc1530',
        fill: false,
        tension: 0.3
      }
    ]
  }

  const quantityChartData = {
    labels,
    datasets: [
      {
        label: 'Qty per Order',
        data: quantities,
        backgroundColor: '#10b981',
        borderRadius: 8,
        barThickness: 15
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: { color: '#fff', font: { size: 13 } }
      },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label}: ${ctx.raw}`
        }
      }
    },
    scales: {
      y: {
        ticks: { color: '#fff' },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      x: {
        ticks: { color: '#fff' },
        grid: { display: false }
      }
    }
  }

  const downloadCSV = () => {
    const header = [
      'Date',
      'Price',
      'Qty',
      'Total Qty',
      'Avg Price',
      'Invested',
      'Fee',
      'Net Invested'
    ]

    const rows = data.orders.map(o => [
      o.date,
      o.price.toFixed(2),
      o.quantity,
      o.total_quantity,
      o.average_price.toFixed(2),
      o.total_investment.toFixed(2),
      o.fee_paid.toFixed(2),
      o.effective_investment.toFixed(2)
    ])

    const csvContent = [
      header.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', 'dca_orders.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const orderDetailsChartData = {
    labels,
    datasets: [
      {
        label: 'Investment ($)',
        data: data.orders.map(o => o.total_investment),
        backgroundColor: '#3b82f6',
        stack: 'Stack 0'
      },
      {
        label: 'Fee ($)',
        data: data.orders.map(o => o.fee_paid),
        backgroundColor: '#f87171',
        stack: 'Stack 0'
      },
      {
        label: 'Quantity',
        data: data.orders.map(o => o.quantity),
        backgroundColor: '#10b981',
        stack: 'Stack 1'
      }
    ]
  }

  const stackedBarOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: { color: '#fff' }
      }
    },
    scales: {
      x: {
        stacked: true,
        ticks: { color: '#fff' },
        grid: { display: false }
      },
      y: {
        stacked: true,
        ticks: { color: '#fff' },
        grid: { color: 'rgba(255,255,255,0.1)' }
      }
    }
  }


  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="rounded-2xl p-6 shadow-xl bg-slate-800/50 text-white backdrop-blur-lg border border-white/10"
    >
      <h2 className="text-2xl font-bold mb-4">DCA Historical Result</h2>
      <p className="text-sm text-gray-300 mb-6">
        Data reflects the most recent update as of: {formattedDate}
      </p>

      {/* Key Info */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <InfoBlock label="Exchange" value={data.exchange} />
        <InfoBlock label="Symbol" value={data.symbol} />
        <InfoBlock label="Start Date" value={data.start_date} />
        <InfoBlock label="Interval (Days)" value={data.interval_days} />
        <InfoBlock label="Investment/Interval" value={`$${data.investment_per_interval}`} />
        <InfoBlock label="# of Orders" value={data.orders.length} />
      </div>

      {/* Table */}
      {/* Order Details Chart */}
      <div className="mt-4 mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">DCA Order Breakdown</h3>
          <button
            onClick={downloadCSV}
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg transition"
          >
            Download CSV
          </button>
        </div>
        <Bar data={orderDetailsChartData} options={stackedBarOptions} />
      </div>


      {/* Charts */}
      <div className="mt-6 mb-10">
        <h3 className="text-lg font-semibold mb-2">Price Trends</h3>
        <Line data={lineChartData} options={chartOptions} />
      </div>

      <div className="mb-10">
        <h3 className="text-lg font-semibold mb-2">Quantity Per Order</h3>
        <Bar data={quantityChartData} options={chartOptions} />
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoBlock label="Total Invested" value={`$${data.summary.total_investment.toFixed(2)}`} />
        <InfoBlock label="Total Quantity" value={data.summary.total_quantity} />
        <InfoBlock label="Avg Price" value={`$${data.summary.average_price.toFixed(2)}`} />
        <InfoBlock label="Current Price" value={`$${data.summary.current_price.toFixed(2)}`} />
        <InfoBlock label="Gross Value" value={`$${data.summary.gross_value.toFixed(2)}`} />
        <InfoBlock label="Net Value" value={`$${data.summary.net_value.toFixed(2)}`} />
        <InfoBlock label="Total Fees" value={`$${data.summary.total_fees.toFixed(2)}`} />
        <InfoBlock label="Profit/Loss" value={`$${data.summary.profit_loss.toFixed(2)}`} />
        <InfoBlock label="ROI (%)" value={`${data.summary.roi.toFixed(2)}%`} />
      </div>

      <p className="text-xs text-gray-300 mt-6">
        This is tentative data and may not reflect precise values.
      </p>
    </motion.div>
  )
}

const InfoBlock = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-400">{label}</p>
    <p className="font-semibold capitalize">{value}</p>
  </div>
)

export default DCAHistoricalResult
